import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { products } from '../data/products';
import debounce from 'lodash/debounce';
import i18next from 'i18next';
import LoginModal from '../components/LoginModal';

export type CartItem = {
  id: number;
  name: string;  // This will be the translated name based on current language
  price: number;
  image: string;
  quantity: number;
};

type CartContextType = {
  cart: CartItem[];
  addToCart: (item: Omit<CartItem, 'quantity'>, quantity: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  removeFromCart: (id: number) => void;
  openCart: () => void;
  closeCart: () => void;
  isCartOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  isModalOpen: boolean;
  clearCart: () => void;
  pendingCartItem: Omit<CartItem, 'quantity'> | null;
  pendingQuantity: number;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

// DTO mapping
const mapToBackendCart = (cart: CartItem[]) => {
  return {
    items: cart.map(item => ({
      productId: item.id,
      productName: item.name,
      price: item.price,
      quantity: item.quantity,
    }))
  };
};

const mapFromBackendCart = (backendCart: { items: any[] }): CartItem[] => {
  const lang = i18next.language as 'en' | 'he';
  return backendCart.items.map(item => {
    const product = products.find(p => p.id === item.productId);
    return {
      id: item.productId,
      name: product?.translations?.[lang]?.name || item.productName,
      price: item.price,
      quantity: item.quantity,
      image: product?.images?.[0] || product?.image || '',
    };
  });
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingCartItem, setPendingCartItem] = useState<Omit<CartItem, 'quantity'> | null>(null);
  const [pendingQuantity, setPendingQuantity] = useState(0);
  const { token, user } = useAuth();

  const syncWithBackend = useCallback(debounce(async (cartToSync: CartItem[]) => {
    if (!token) return;
    try {
      await fetch('/api/cart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(mapToBackendCart(cartToSync)),
      });
    } catch (error) {
      console.error("Failed to sync cart with backend:", error);
    }
  }, 1000), [token]);

  // Effect to load cart from backend when user logs in
  useEffect(() => {
    const loadCart = async () => {
      if (!token) {
        setCart([]);
        return;
      }

      try {
        const response = await fetch('/api/cart', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setCart(mapFromBackendCart(data));
        }
      } catch (error) {
        console.error("Failed to load cart:", error);
      }
    };

    loadCart();
  }, [token]);

  // Effect to sync cart with backend
  useEffect(() => {
    if (cart.length > 0) {
      syncWithBackend(cart);
    }
  }, [cart, syncWithBackend]);

  const addToCart = async (item: Omit<CartItem, 'quantity'>, quantity: number) => {
    // Check if user is authenticated
    if (!user) {
      // Store the pending item and show login modal
      setPendingCartItem(item);
      setPendingQuantity(quantity);
      setShowLoginModal(true);
      return;
    }

    // User is authenticated, add to cart normally
    setCart((prev) => {
      const existing = prev.find((i) => i.id === item.id);
      if (existing) {
        return prev.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      return [...prev, { ...item, quantity }];
    });
    setIsCartOpen(true);
  };

  const handleLoginSuccess = () => {
    // After successful login, add the pending item to cart
    if (pendingCartItem && pendingQuantity > 0) {
      setCart((prev) => {
        const existing = prev.find((i) => i.id === pendingCartItem.id);
        if (existing) {
          return prev.map((i) =>
            i.id === pendingCartItem.id ? { ...i, quantity: i.quantity + pendingQuantity } : i
          );
        }
        return [...prev, { ...pendingCartItem, quantity: pendingQuantity }];
      });
      setIsCartOpen(true);
    }
    // Clear pending items
    setPendingCartItem(null);
    setPendingQuantity(0);
  };

  const updateQuantity = async (id: number, quantity: number) => {
    setCart((prev) => {
      return prev.map((item) =>
        item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item
      );
    });
    try {
      await fetch('/api/cart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(mapToBackendCart(cart)),
      });
    } catch (error) {
      console.error('Failed to sync cart with backend after updateQuantity', error);
    }
  };

  const removeFromCart = async (id: number) => {
    setCart((prev) => {
      const newCart = prev.filter((item) => item.id !== id);
      if (token) {
        fetch('/api/cart', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(mapToBackendCart(newCart)),
        });
      }
      return newCart;
    });
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const openModal = () => {
    // Check if user is authenticated for quick buy
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  const clearCart = () => {
    setCart([]);
    // If using localStorage or backend, clear there as well
    localStorage.removeItem('cart');
    // Optionally, sync with backend if needed
    if (token) {
      fetch('/api/cart', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
    }
  };

  return (
    <CartContext.Provider
      value={{ 
        cart, 
        addToCart, 
        updateQuantity, 
        removeFromCart, 
        openCart, 
        closeCart, 
        isCartOpen, 
        openModal, 
        closeModal, 
        isModalOpen, 
        clearCart,
        pendingCartItem,
        pendingQuantity
      }}
    >
      {children}
      <LoginModal 
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
      />
    </CartContext.Provider>
  );
}; 